// Page controllers (ARCHITECTURE §2, D2, §5.2). Every page folder has a
// `pages/<folder>/controller.ts` default-exporting the page's controller hook
// (`() => PageVMMap[K]`).
//
// Controllers for classic's four eager pages (home, schedule, players, teams)
// are bundled eagerly, exactly like those pages; every other controller is
// its own lazy chunk, loaded in parallel with the page's view chunk, so the
// entry bundle stays within budget B1 and the controller still mounts
// exactly when the view does (inside the lazy boundary, §5.2).

import type { PageVMMap, SitePageKey } from '../template/pages'
import { PAGE_FOLDERS, PAGE_KEYS } from '../template/pages'

export type PageController<K extends SitePageKey> = () => PageVMMap[K]

type ControllerModule = { default: PageController<SitePageKey> }

const eagerModules = import.meta.glob<ControllerModule>('./{home,schedule,players,teams}/controller.ts', { eager: true })
const lazyModules = import.meta.glob<ControllerModule>(['./*/controller.ts', '!./{home,schedule,players,teams}/controller.ts'])

const folderOf = (path: string) => /^\.\/([^/]+)\/controller\.ts$/.exec(path)?.[1]
const keyOfFolder = (folder: string | undefined) => PAGE_KEYS.find((k) => PAGE_FOLDERS[k] === folder)

const EAGER: Partial<Record<SitePageKey, PageController<SitePageKey>>> = {}
for (const [path, mod] of Object.entries(eagerModules)) {
  const key = keyOfFolder(folderOf(path))
  if (key) EAGER[key] = mod.default
}

const LAZY: Partial<Record<SitePageKey, () => Promise<PageController<SitePageKey>>>> = {}
for (const [path, load] of Object.entries(lazyModules)) {
  const key = keyOfFolder(folderOf(path))
  if (key) LAZY[key] = () => load().then((m) => m.default)
}

/** The controller hook if it's bundled eagerly; null when it must be loaded. */
export function eagerController<K extends SitePageKey>(page: K): PageController<K> | null {
  return (EAGER[page] as PageController<K> | undefined) ?? null
}

/** The page's controller hook, eager or lazy. */
export function loadController<K extends SitePageKey>(page: K): Promise<PageController<K>> {
  const eager = eagerController(page)
  if (eager) return Promise.resolve(eager)
  const load = LAZY[page]
  if (!load) return Promise.reject(new Error(`no controller for page "${page}"`))
  return load() as Promise<PageController<K>>
}

/** The page keys that have a controller module (every key; the contract test checks). */
export function controllerKeys(): SitePageKey[] {
  return PAGE_KEYS.filter((k) => Boolean(EAGER[k] || LAZY[k]))
}
